/**
 * The single definition of what a "to complete" hole in the HACCP manual looks
 * like. Both sides import it: the backend emits and gates on it, the frontend
 * decorates and counts it. Two copies of this regex would drift, and the drift
 * would be silent — a hole nobody highlights is a hole that reaches a signed PDF.
 */

export type ManualPlaceholderLocale = 'it' | 'en';

export const PLACEHOLDER_TOKEN: Record<ManualPlaceholderLocale, string> = {
  it: 'DA COMPLETARE',
  en: 'TO COMPLETE',
};

export interface PlaceholderMatch {
  /** Offset of the `[` in the searched string. */
  start: number;
  /** Offset just past the `]`. */
  end: number;
  /**
   * What the manual says is missing, trimmed. Null for the bare `[DA COMPLETARE]`
   * form that drafts generated before this feature still carry — those are real
   * holes and must still be found, they just can't name themselves.
   */
  what: string | null;
  /** The literal matched text, e.g. `[DA COMPLETARE: la partita IVA]`. */
  raw: string;
}

/**
 * `what` is optional on purpose: legacy drafts emit the bare token for exactly
 * the most common holes (P.IVA, tax code, site, HACCP responsible, region), so a
 * regex that required `: <what>` would silently skip them.
 */
function placeholderRe(locale: ManualPlaceholderLocale): RegExp {
  return new RegExp(`\\[${PLACEHOLDER_TOKEN[locale]}(?::\\s*([^\\]]*))?\\]`, 'g');
}

/** Every placeholder in `text`, in document order. */
export function findPlaceholderMatches(
  text: string,
  locale: ManualPlaceholderLocale,
): PlaceholderMatch[] {
  const matches: PlaceholderMatch[] = [];
  for (const m of text.matchAll(placeholderRe(locale))) {
    const what = m[1]?.trim();
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      what: what ? what : null,
      raw: m[0],
    });
  }
  return matches;
}

/**
 * True if `text` contains any trace of the token, well-formed or not.
 *
 * Deliberately looser than `findPlaceholderMatches`: this backs the finalize
 * gate, where a placeholder the user broke while editing (a deleted `]`, a bold
 * applied across the middle) is still an unfilled hole — and one the decoration
 * can no longer highlight, so it is exactly the case the gate exists to catch.
 * Costs a false positive if legitimate prose ever says "DA COMPLETARE"; that
 * costs one confirmation, whereas a miss costs a signed manual with a hole.
 */
export function hasPlaceholderResidue(text: string, locale: ManualPlaceholderLocale): boolean {
  return text.includes(PLACEHOLDER_TOKEN[locale]);
}
